const DEFAULT_SIZE = 15;
const WIN_LENGTH = 5;

function inBounds(row, col, size) {
  return row >= 0 && row < size && col >= 0 && col < size;
}

function sideToPiece(side) {
  return side === 'w' ? 'X' : 'O';
}

function pieceToSide(piece) {
  return piece === 'X' ? 'w' : piece === 'O' ? 'b' : null;
}

export class CaroGame {
  constructor(size = DEFAULT_SIZE) {
    this.size = Number.isInteger(size) && size >= 10 && size <= 20 ? size : DEFAULT_SIZE;
    this.board = Array(this.size * this.size).fill(null);
    this.turn = 'w';
    this.history = [];
    this.winner = null;
    this.winningLine = [];
    this.draw = false;
  }

  get finished() {
    return Boolean(this.winner || this.draw);
  }

  move(from, to = from) {
    const idx = Number(to);
    if (!Number.isInteger(idx) || idx < 0 || idx >= this.board.length || this.board[idx] || this.finished) return null;

    const side = this.turn;
    const piece = sideToPiece(side);
    this.board[idx] = piece;

    const winningLine = this.findWinningLine(idx, piece);
    if (winningLine.length >= WIN_LENGTH) {
      this.winner = side;
      this.winningLine = winningLine;
    } else if (this.board.every(Boolean)) {
      this.draw = true;
    }

    const row = Math.floor(idx / this.size);
    const col = idx % this.size;
    const notation = `${piece} ${String.fromCharCode(65 + col)}${row + 1}`;
    const move = { from: idx, to: idx, piece, captured: null, side };
    this.history.push({ move, notation });

    if (!this.finished) this.turn = side === 'w' ? 'b' : 'w';
    return move;
  }

  undo() {
    const entry = this.history.pop();
    if (!entry) return null;
    this.board[entry.move.to] = null;
    this.turn = entry.move.side;
    this.winner = null;
    this.winningLine = [];
    this.draw = false;
    return entry.move;
  }

  legalMoves() {
    if (this.finished) return [];
    const moves = [];
    for (let idx = 0; idx < this.board.length; idx++) {
      if (!this.board[idx]) moves.push({ from: idx, to: idx, piece: sideToPiece(this.turn) });
    }
    return moves;
  }

  findWinningLine(idx, piece = this.board[idx]) {
    if (!piece) return [];
    const row = Math.floor(idx / this.size);
    const col = idx % this.size;
    const directions = [[1, 0], [0, 1], [1, 1], [1, -1]];

    for (const [dr, dc] of directions) {
      const line = [idx];

      for (const sign of [-1, 1]) {
        let r = row + dr * sign;
        let c = col + dc * sign;
        while (inBounds(r, c, this.size) && this.board[r * this.size + c] === piece) {
          if (sign < 0) line.unshift(r * this.size + c);
          else line.push(r * this.size + c);
          r += dr * sign;
          c += dc * sign;
        }
      }

      if (line.length >= WIN_LENGTH) return line;
    }

    return [];
  }

  status() {
    return {
      turn: this.turn,
      winner: this.winner,
      draw: this.draw,
      finished: this.finished,
      winningLine: this.winningLine.slice(),
      check: false,
      checkmate: false,
      stalemate: false,
      fifty: false,
      repetition: false,
      insufficient: false
    };
  }
}

function candidateIndices(game) {
  const occupied = [];
  for (let i = 0; i < game.board.length; i++) if (game.board[i]) occupied.push(i);
  if (!occupied.length) {
    const center = Math.floor(game.size / 2);
    return [center * game.size + center];
  }

  const set = new Set();
  for (const idx of occupied) {
    const r0 = Math.floor(idx / game.size);
    const c0 = idx % game.size;
    for (let dr = -2; dr <= 2; dr++) {
      for (let dc = -2; dc <= 2; dc++) {
        const r = r0 + dr;
        const c = c0 + dc;
        if (!inBounds(r, c, game.size)) continue;
        const candidate = r * game.size + c;
        if (!game.board[candidate]) set.add(candidate);
      }
    }
  }
  return [...set];
}

function lineScore(game, idx, piece) {
  const row = Math.floor(idx / game.size);
  const col = idx % game.size;
  const directions = [[1, 0], [0, 1], [1, 1], [1, -1]];
  let score = 0;

  for (const [dr, dc] of directions) {
    let count = 1;
    let openEnds = 0;
    for (const sign of [-1, 1]) {
      let r = row + dr * sign;
      let c = col + dc * sign;
      while (inBounds(r, c, game.size) && game.board[r * game.size + c] === piece) {
        count++;
        r += dr * sign;
        c += dc * sign;
      }
      if (inBounds(r, c, game.size) && !game.board[r * game.size + c]) openEnds++;
    }
    if (count >= 5) score += 1_000_000;
    else if (count === 4) score += openEnds === 2 ? 80_000 : 20_000;
    else if (count === 3) score += openEnds === 2 ? 8_000 : 1_600;
    else if (count === 2) score += openEnds === 2 ? 700 : 160;
    else score += openEnds * 10;
  }

  return score;
}

export function chooseCaroBotMove(game, level = 3) {
  if (!(game instanceof CaroGame) || game.finished) return null;
  const candidates = candidateIndices(game);
  if (!candidates.length) return null;

  const botPiece = sideToPiece(game.turn);
  const opponentPiece = botPiece === 'X' ? 'O' : 'X';
  const difficulty = Math.max(1, Math.min(5, Number(level) || 3));

  // Tactical invariant: never miss a legal one-move win, regardless of
  // difficulty. Heuristic weights must not override an immediately terminal move.
  for (const idx of candidates) {
    game.board[idx] = botPiece;
    const winsNow = game.findWinningLine(idx, botPiece).length >= WIN_LENGTH;
    game.board[idx] = null;
    if (winsNow) return { from: idx, to: idx };
  }

  if (difficulty === 1) {
    const idx = candidates[Math.floor(Math.random() * candidates.length)];
    return { from: idx, to: idx };
  }

  let scored = candidates.map(idx => {
    game.board[idx] = botPiece;
    const attack = lineScore(game, idx, botPiece);
    game.board[idx] = opponentPiece;
    const defend = lineScore(game, idx, opponentPiece);
    game.board[idx] = null;

    const r = Math.floor(idx / game.size);
    const c = idx % game.size;
    const center = (game.size - 1) / 2;
    const centerBias = Math.max(0, game.size - (Math.abs(r - center) + Math.abs(c - center)));
    const defenseWeight = difficulty >= 4 ? 1.08 : 0.92;
    const noise = difficulty <= 2 ? Math.random() * 900 : difficulty === 3 ? Math.random() * 120 : 0;
    return { idx, score: attack + defend * defenseWeight + centerBias + noise };
  });

  scored.sort((a, b) => b.score - a.score);
  const poolSize = difficulty === 2 ? Math.min(5, scored.length) : difficulty === 3 ? Math.min(2, scored.length) : 1;
  const pick = scored[Math.floor(Math.random() * poolSize)];
  return pick ? { from: pick.idx, to: pick.idx } : null;
}

export { DEFAULT_SIZE as CARO_SIZE, WIN_LENGTH as CARO_WIN_LENGTH, pieceToSide as caroPieceSide };
